import { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Sparkle, Globe, ShieldSlash, CheckCircle } from "@phosphor-icons/react";

const OPTIONS = [
  {
    key: "full",
    label: "Full AI",
    icon: Sparkle,
    blurb: "AI assists everywhere — including tools that work with your confidential asset and evidence data.",
    accent: "border-emerald-400 bg-emerald-50 text-emerald-800",
    dot: "text-emerald-600",
  },
  {
    key: "public",
    label: "Public-information only",
    icon: Globe,
    blurb: "AI runs only on public-standard tools (STaR, ClaroLens, IRA, drafting). Anything that reads your assets or evidence is hard-blocked.",
    accent: "border-sky-400 bg-sky-50 text-sky-800",
    dot: "text-sky-600",
  },
  {
    key: "off",
    label: "AI off",
    icon: ShieldSlash,
    blurb: "No AI anywhere. Every AI action across the platform is disabled and no content is sent to any AI provider.",
    accent: "border-amber-400 bg-amber-50 text-amber-800",
    dot: "text-amber-600",
  },
];

export default function AiSettings() {
  const { aiScope, setAiScope, setAiEnabled } = useAuth();
  const [saving, setSaving] = useState(false);

  const choose = async (scope) => {
    if (scope === aiScope || saving) return;
    setSaving(true);
    const prev = aiScope;
    setAiScope(scope); // optimistic
    setAiEnabled(scope !== "off");
    try {
      const { data } = await api.put("/admin/ai-settings", { scope });
      setAiScope(data.ai_scope);
      setAiEnabled(data.ai_enabled);
      const label = OPTIONS.find((o) => o.key === data.ai_scope)?.label;
      toast.success(`AI control set to “${label}” for this entity.`);
    } catch (e) {
      setAiScope(prev); // revert
      setAiEnabled(prev !== "off");
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update AI controls.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-border bg-card p-6" data-testid="ai-settings-card">
      <div className="flex gap-3">
        <Sparkle size={22} className="text-primary mt-0.5 shrink-0" weight="fill" />
        <div>
          <h3 className="font-display font-bold text-lg">AI &amp; Data Controls</h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            You decide how much AI runs in this entity — and, critically, whether AI is ever allowed to touch your
            confidential asset and evidence data. Choose the level your organization is comfortable with.
          </p>
        </div>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3" data-testid="ai-scope-options">
        {OPTIONS.map((o) => {
          const active = aiScope === o.key;
          const Icon = o.icon;
          return (
            <button
              key={o.key}
              data-testid={`ai-scope-${o.key}`}
              onClick={() => choose(o.key)}
              disabled={saving}
              className={`text-left rounded-none border p-4 transition-colors disabled:opacity-60 ${
                active ? o.accent : "border-border bg-background hover:border-primary/50"
              }`}
            >
              <div className="flex items-center justify-between">
                <Icon size={20} className={active ? o.dot : "text-muted-foreground"} weight={active ? "fill" : "regular"} />
                {active && <CheckCircle size={18} className={o.dot} weight="fill" data-testid={`ai-scope-${o.key}-active`} />}
              </div>
              <div className="font-display font-bold text-sm mt-2">{o.label}</div>
              <p className="text-xs text-muted-foreground mt-1 leading-snug">{o.blurb}</p>
            </button>
          );
        })}
      </div>

      {aiScope === "public" && (
        <div className="mt-4 border border-sky-300 bg-sky-50 text-sky-900 text-sm px-3 py-2" data-testid="ai-scope-public-note">
          <b>Public-information mode is on.</b> AI-assisted tools that read your evidence repository, asset inventories,
          or incident data are blocked. Public-standard AI (STaR, ClaroLens, IRA, drafting) still works.
        </div>
      )}
      {aiScope === "off" && (
        <div className="mt-4 border border-amber-300 bg-amber-50 text-amber-800 text-sm px-3 py-2" data-testid="ai-scope-off-note">
          AI is currently <b>off</b>. AI buttons are grayed out across the app and all AI requests are blocked.
        </div>
      )}
    </div>
  );
}
